<script setup lang="ts">
import type { ProductSchema } from '@laam/cms-shared';
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	type CarouselApi,
} from '@laam/ui/base';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import { watchOnce } from '@vueuse/core';
import { useCarouselMedia } from '~/components/product-detail/media-carousel/useCarouselMedia';
import ImageViewer from '../ImageViewer.vue';
import ImageViewerMobile from '../ImageViewerMobile.vue';
import CarouselIndex from './CarouselIndex.vue';
import CarouselSlide from './CarouselSlide.vue';
import CarouselThumbnail from './CarouselThumbnail.vue';
import { MediaType } from './types';

interface Props {
	productData: ProductSchema;
	parent: string;
	alt: string;
}
const props = defineProps<Props>();
const isImageViewerOpen = ref<boolean>(false);

const emblaMainApi = ref<CarouselApi>();
const emblaThumbnailApi = ref<CarouselApi>();
const selectedIndex = ref(0);

const onOpenImageViewer = () => {
	if (media[selectedIndex.value]?.type === MediaType.VIDEO) return;
	isImageViewerOpen.value = true;
};

function onSelect() {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	selectedIndex.value = emblaMainApi.value.selectedScrollSnap();
	emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap());
}

function onThumbClick(index: number) {
	if (!emblaMainApi.value || !emblaThumbnailApi.value) return;
	emblaMainApi.value.scrollTo(index);
}

watchOnce(emblaMainApi, (emblaMainApi) => {
	if (!emblaMainApi) return;

	onSelect();
	emblaMainApi.on('select', onSelect);
	emblaMainApi.on('reInit', onSelect);
});

const media = useCarouselMedia({
	images: props.productData.media,
	videos: props.productData.videos,
	contentVideos: props.productData.content_videos,
});

const poster = computed(() => props.productData.media[0]?.src ?? '');
</script>

<template>
	<div class="space-y-7xl">
		<Carousel
			class="relative select-none"
			:opts="{ loop: true }"
			@init-api="(val) => (emblaMainApi = val)"
		>
			<div>
				<CarouselContent>
					<CarouselItem
						v-for="(slide, index) in media"
						:key="index"
						class="relative"
						@click="onOpenImageViewer"
					>
						<CarouselSlide
							:type="slide.type"
							:src="slide.src"
							:alt="props.alt"
							:is-active="index === selectedIndex"
							:poster="slide?.poster ?? poster"
							:product-id="props.productData.shopify_id"
							:stock-status="props.productData.stock_status"
						/>
						<CarouselIndex :media="media" :index="index" />
					</CarouselItem>
				</CarouselContent>
				<ImageViewerMobile
					v-if="isMobileView()"
					v-model="isImageViewerOpen"
					:media="media"
					:alt="props.alt"
					:index="selectedIndex"
					:fallback-poster="poster"
				/>
				<ImageViewer
					v-else
					v-model="isImageViewerOpen"
					:media="media"
					:alt="props.alt"
					:index="selectedIndex"
					:fallback-poster="poster"
				/>
			</div>
			<CarouselPrevious class="navigation_button">
				<PhCaretLeft weight="bold" />
			</CarouselPrevious>
			<CarouselNext class="navigation_button">
				<PhCaretRight weight="bold" />
			</CarouselNext>
		</Carousel>

		<Carousel
			class="lg:block hidden relative select-none"
			:opts="{ loop: true, dragFree: true }"
			@init-api="(val) => (emblaThumbnailApi = val)"
		>
			<CarouselContent
				class="flex gap-1 ml-0"
				:class="{ 'justify-center': media.length <= 5 }"
			>
				<CarouselItem
					v-for="(slide, index) in media"
					:key="index"
					class="!p-0 !pr-md basis-[22.5%] cursor-pointer"
					@click="onThumbClick(index)"
				>
					<CarouselThumbnail
						:type="slide.type"
						:src="
							slide.type === MediaType.IMAGE
								? slide.src
								: slide?.poster ?? poster
						"
						:alt="alt"
						:is-active="index === selectedIndex"
					/>
				</CarouselItem>
			</CarouselContent>
		</Carousel>
	</div>
</template>

<style scoped>
.navigation_button {
	@apply !size-10xl !flex top-[50%] -translate-y-[50%] [&_svg]:size-5xl;
}
</style>
